import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '~/lib/api-client';
import { toast } from 'sonner';
import { meKeys } from '~/lib/query-keys';

export interface CurrentTenantResponse {
  settings: {
    zitadelOrgId: string;
    preferences?: { timezone?: string; displayName?: string };
    features: {
      reflectionEnabled: boolean;
      rateLimitRpm: number;
      truthGuardEnabled?: boolean;
    };
  } | null;
  identity: { name?: string; status?: string } | null;
  /** Deployment capabilities (env-driven, not per-tenant). */
  capabilities?: { coding: boolean };
}

/** True when this deployment can run coding sessions (needs the sandbox on). */
export function useCodingEnabled(): boolean {
  const { data } = useCurrentTenant();
  return data?.capabilities?.coding ?? false;
}

export const tenantKeys = {
  current: () => ['tenants', 'current'] as const,
};

export function useCurrentTenant() {
  return useQuery({
    queryKey: tenantKeys.current(),
    queryFn: () => api.get<CurrentTenantResponse>('/api/admin/tenants/current'),
    staleTime: 60_000,
  });
}

export interface OnboardedResponse {
  onboarded: boolean;
  displayName?: string;
}

/**
 * Has the calling user finished the identity step of onboarding? Backed by
 * whether their graph entity exists. Cached for the session — invalidated by
 * the onboarding identity-save mutation AND by any other write to the
 * caller's own graph self-entity (e.g. a Memory-page rename via
 * `entity-detail.tsx`), since both change the same underlying displayName.
 */
export function useIsOnboarded() {
  return useQuery({
    queryKey: meKeys.onboarded(),
    queryFn: () => api.get<OnboardedResponse>('/api/admin/me/onboarded'),
    staleTime: Infinity,
  });
}

/**
 * Current-user display name. Reads from the auth-gate's `/api/auth/me`
 * cache (populated at app boot in app-layout) — the IdP's `name` /
 * `preferred_username` / `given_name` claim carried through the session
 * cookie. Falls back to the tenant onboarding identity, then to 'Me'.
 *
 * Returns the RAW IdP name uninterpreted — the onboarding wizard's identity
 * step (step-identity.tsx) relies on that to detect "looks like a real
 * name" for its own placeholder logic. For a casual/preferred name (e.g.
 * the home greeting), use useGreetingName() instead.
 */
export function useUserDisplayName(): string {
  const queryClient = useQueryClient();
  // Called before any early return: a hook behind one ran only while the
  // cached sign-in had no name, so the hook count changed the moment it
  // arrived and crashed a page that mounts before it (the onboarding setup
  // chat, FRONT-3223).
  const { data } = useCurrentTenant();
  const me = queryClient.getQueryData<{ name?: string; email?: string }>(['auth', 'me']);
  if (me?.name?.trim()) return me.name.trim();
  if (me?.email?.trim()) return me.email.trim();
  return data?.identity?.name?.trim() || 'Me';
}

/**
 * Casual display name for surfaces like the home greeting (FRONT-857).
 * Prefers the preferred name the user typed into the onboarding wizard's
 * identity step (persisted on their graph self-entity), since that's an
 * explicit "call me this" choice — falls back to the first token of the
 * raw IdP name for accounts that onboarded before that step existed.
 */
export function useGreetingName(): string {
  const { data: onboarded } = useIsOnboarded();
  const rawName = useUserDisplayName();
  if (onboarded?.displayName?.trim()) return onboarded.displayName.trim();
  return rawName.split(/\s+/)[0];
}

/** FRONT-1357: one entry in the avatar-menu tenant switcher. */
export interface SwitchableTenant {
  orgId: string;
  label: string;
  home: boolean;
  /** eddy.enable state of that org — false renders muted + toasts on click. */
  enabled: boolean;
}

export interface SwitchableTenantsResponse {
  current: string | null;
  tenants: SwitchableTenant[];
}

/**
 * The orgs the session's token can act in (home plus granted). Empty while
 * grant-based tenancy is off server-side, so callers can simply render
 * nothing. Cached briefly: the set only changes when grants change.
 */
export function useSwitchableTenants() {
  return useQuery({
    queryKey: ['tenants', 'switchable'],
    queryFn: () => api.get<SwitchableTenantsResponse>('/api/auth/tenants'),
    staleTime: 60_000,
  });
}

/**
 * FRONT-1357: the tenant-switch mutation. Owns the session-rescoping call
 * and the deliberate full reload (every org-keyed store, query cache and
 * socket must re-scope), so UI components stay presentation-only.
 */
const SWITCH_ERROR_COPY: Record<string, string> = {
  org_not_enabled: "EDDY is not enabled for this tenant yet.",
  tenant_access_denied: "You do not have access to this tenant.",
  relogin_required: "Please sign in again to switch tenants.",
  mfa_policy_unavailable: "Couldn't check this tenant's sign-in rules. Please try again in a moment.",
};

/**
 * What a failed switch should do: follow a login URL, or show a message.
 * FRONT-2359: `step_up_required` means the target org forces MFA this session
 * never proved; the server-built login for that org proves it and lands
 * there. Only a same-origin `/login` path is followed.
 */
export function switchFailure(err: unknown): { redirect: string } | { message: string } {
  const body = err instanceof ApiError && err.body && typeof err.body === "object"
    ? (err.body as { error?: string; loginUrl?: string })
    : {};
  const code = String(body.error ?? "");
  if (code === "step_up_required" && body.loginUrl?.startsWith("/login?")) {
    return { redirect: body.loginUrl };
  }
  return { message: SWITCH_ERROR_COPY[code] ?? "Tenant switch failed. Please try again." };
}

export function useSwitchTenant() {
  return async (orgId: string): Promise<void> => {
    try {
      await api.post("/api/auth/switch-tenant", { orgId });
      window.location.assign("/");
    } catch (err) {
      const failure = switchFailure(err);
      if ("redirect" in failure) {
        window.location.assign(failure.redirect);
        return;
      }
      toast.error(failure.message);
      console.error("tenant switch failed:", err);
    }
  };
}
